// AddToWatchlistModal.jsx
import React, { useState, useContext,useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import { URL } from '../../../common/api/urls';
import { useAxiosWithAuth } from '../../../common/api/hooks/useAxiosWithAuth';
import { UserSettingsContext } from '../../../setup/routes-manager/RequireAuth';

const AddToWatchlistModal = ({ open, onClose, instrument, instrumentName }) => {
  const {
    watchlists,
    setWatchlists,
    refreshWatchlists,
    triggerWatchlistRefresh
  } = useContext(UserSettingsContext);
  const [, , , , , executeUpdate] = useAxiosWithAuth();

  const [initialState, setInitialState] = useState({});
  const [checkedState, setCheckedState] = useState({});
  const [saving, setSaving] = useState(false);
  const hasSelectedWatchlist = Object.values(checkedState).some(Boolean);

  useEffect(() => {
    if (!open || !watchlists || !instrument) return;

    const state = Object.fromEntries(
        Object.entries(watchlists).map(([name, instruments]) => [
            name,
            instruments.includes(instrument),
        ])
    );

    setInitialState(state);
    setCheckedState(state);
}, [open, watchlists, instrument]);


  const handleToggle = (name) => {
    setCheckedState(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const handleUpdate = async () => {
    if (!watchlists) return;
    if (!hasSelectedWatchlist) return;
    setSaving(true);

    const changedNames = Object.keys(checkedState).filter(
      name => checkedState[name] !== initialState[name]
    );

    if (changedNames.length === 0) {
      setSaving(false);
      onClose();
      return;
    }
        // Fires one POST per changed watchlist. Each payload is independent
    // (full-array replace, keyed by name) so order doesn't matter.
    // NOTE: doesn't wait for/confirm individual success — see caveat above
    // re: useAxiosWithAuth. If you need per-call confirmation, this needs
    // one hook instance per call or a real queue, not this loop.
    await Promise.all(
      changedNames.map(name => {
        const currentList = watchlists[name] || [];
        const newList = checkedState[name]
          ? (currentList.includes(instrument) ? currentList : [...currentList, instrument])
          : currentList.filter(i => i !== instrument);
        return executeUpdate(URL.CREATE_WATCHLIST, "POST", { [name]: newList });
      })
    );

    setSaving(false);
    if (setWatchlists) {
      setWatchlists((prev) => {
        const nextWatchlists = { ...(prev || watchlists || {}) };
        changedNames.forEach((name) => {
          const currentList = nextWatchlists[name] || [];
          nextWatchlists[name] = checkedState[name]
            ? (currentList.includes(instrument) ? currentList : [...currentList, instrument])
            : currentList.filter((item) => item !== instrument);
        });
        return nextWatchlists;
      });
    }
    refreshWatchlists();
    triggerWatchlistRefresh();
    onClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={handleKeyDown}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 360,
          borderRadius: 4,
          overflow: 'hidden',
          textAlign: 'left',
          bgcolor: '#0f1f3d',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.35)',
        },
      }}
    >
      <DialogTitle sx={{ px: 3, pt: 3, pb: 0.5, fontSize: '1.1rem', fontWeight: 700, color: 'common.white' }}>
        Add {instrumentName || instrument}
      </DialogTitle>

      <Typography sx={{ px: 3, pb: 0, color: 'rgba(255, 255, 255, 0.65)', fontSize: '0.875rem' }}>
        Select the watchlists you want to add this to..
      </Typography>

      <DialogContent sx={{ px: 3, pb: 3 }}>
        {!watchlists ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : Object.keys(watchlists).length === 0 ? (
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            No watchlists found.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
            {Object.keys(watchlists).map(name => (
              <Box
                key={name}
                onClick={() => handleToggle(name)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  px: 1.75,
                  py: 1.2,
                  borderRadius: 2.5,
                  border: '1px solid',
                  borderColor: checkedState[name] ? 'rgba(98, 160, 255, 0.8)' : 'rgba(255, 255, 255, 0.1)',
                  bgcolor: checkedState[name] ? 'rgba(98, 160, 255, 0.12)' : 'rgba(255, 255, 255, 0.03)',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    borderColor: 'rgba(98, 160, 255, 0.75)',
                    bgcolor: 'rgba(255, 255, 255, 0.06)',
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'common.white' }}>
                  {name}
                </Typography>
                <Checkbox
                  size="small"
                  checked={!!checkedState[name]}
                  onChange={() => handleToggle(name)}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ p: 0.5, color: 'rgba(255, 255, 255, 0.6)', '&.Mui-checked': { color: '#62a0ff' } }}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button
          fullWidth
          variant="outlined"
          onClick={onClose}
          disabled={saving}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            color: 'common.white',
            borderColor: 'rgba(255, 255, 255, 0.18)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.3)',
              bgcolor: 'rgba(255, 255, 255, 0.06)',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          variant="contained"
          onClick={handleUpdate}
          disabled={saving || !watchlists || !hasSelectedWatchlist}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            bgcolor: '#62a0ff',
            '&:hover': { bgcolor: '#4f8df0' },
            '&.Mui-disabled': {
              bgcolor: 'rgba(255, 255, 255, 0.12)',
              color: 'rgba(255, 255, 255, 0.35)',
            },
          }}
        >
          {saving ? <CircularProgress size={18} color="inherit" /> : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToWatchlistModal;